/* rv-reveal: a brief, subtle fade-up as content scrolls into view, on every page (Cris, 2026-10-11: "similar to
   Cadence … very brief and subtle, where the words are popping up rather than all of it being stuck to the page").
   Cadence uses AOS fade-up (700ms, ease-out-quart, section heads first, then cards ~100ms apart); this is the same
   idea without a library, at a smaller distance.

   - Section headings and intros fade up on their own; card rows stagger (90ms apart, capped).
   - Anything already on screen when the page loads is left alone (no flash, no hidden LCP).
   - Skipped: reduced motion, the theme editor, browsers without IntersectionObserver, and sections that already move
     (home hero, proof strip marquee, the Short List, the buy box form, dialogs, drawers, the popup, Judge.me widgets).
   - Only opacity and the `translate` property animate (never `transform`, so hover and tilt transforms keep working);
     the attribute comes off after the fade, so each element's own transitions come back.
   Styles: assets/rv-base.css ("Scroll reveal"). */
(() => {
  if (!('IntersectionObserver' in window)) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const SINGLE = [
    'main h2',
    'main .rv-lead',
    'main .rv-why-cards__sub',
    'main .rv-scoop__sub',
    'main .rv-faq__intro',
    'main .rv-tested__note',
    'main .rv-versus__art',
  ];
  const GROUP = [
    '.rv-why-cards__card',
    '.rv-ing__card',
    '.rv-scoop__job',
    '.rv-how-to__step',
    '.rv-tested__point',
    '.rv-inside__row',
  ];
  const SKIP = [
    '.rv-section-home-hero',
    '.rv-section-proof-strip',
    '.rv-short-list__section',
    '.rv-short-list',
    'form',
    'dialog',
    'details:not([open]) .rv-acc__body',
    '.rv-overlay',
    '.rv-drawer',
    'cart-drawer',
    '.jdgm-widget',
    '[class*="jdgm"]',
    '[data-rv-no-reveal]',
  ].join(',');

  const start = () => {
    const fold = window.innerHeight * 0.92;
    const gsel = GROUP.join(',');
    const seen = new Set();
    const cands = [];
    const pick = (el, delay) => {
      if (seen.has(el) || el.closest(SKIP)) return;
      seen.add(el);
      cands.push([el, delay]);
    };
    document.querySelectorAll(SINGLE.join(',')).forEach((el) => pick(el, 0));
    document.querySelectorAll(gsel).forEach((el) => {
      const sibs = [...el.parentElement.children].filter((c) => c.matches(gsel));
      pick(el, Math.min(sibs.indexOf(el), 5) * 90);
    });
    // all reads first, then all writes: one layout instead of one per element (speed audit 2026-10-11)
    const tops = cands.map(([el]) => el.getBoundingClientRect().top);
    const items = [];
    cands.forEach(([el, delay], i) => {
      if (tops[i] < fold) return; // already on screen: leave it be
      el.style.setProperty('--rv-r-d', `${delay}ms`);
      el.setAttribute('data-rv-r', '');
      items.push(el);
    });
    if (!items.length) return;

    const done = (el) => {
      el.removeAttribute('data-rv-r');
      el.classList.remove('rv-r-in');
      el.style.removeProperty('--rv-r-d');
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const el = e.target;
          io.unobserve(el);
          el.classList.add('rv-r-in');
          const d = parseFloat(el.style.getPropertyValue('--rv-r-d')) || 0;
          setTimeout(() => done(el), 750 + d);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
